import { User, CertificationType } from '../types';

export interface SuggestionItem {
  id: string;
  type: 'guide' | 'skills' | 'quiz' | 'exam' | 'simulator' | 'flashcards' | 'podcasts' | 'tutor' | 'search';
  title: string;
  badge: string;
  badgeColor: string;
  description: string;
  rationale: string; // The "Clinical Judgement Rationale" based on standard metrics
  actionText: string;
  targetUrl: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced' | 'Critical Drill';
  timeEstimate: string;
  engagementXP: number;
}

export const SuggestionService = {
  /**
   * Generates highly personalized suggestions based on user scores, history, 
   * missed questions, and current academic completion state.
   */
  generateSuggestions: (
    user: User | null,
    activeCert: CertificationType | null,
    context: {
      source: 'guide' | 'quiz' | 'exam' | 'simulator' | 'skills';
      score?: number;
      moduleId?: string;
      moduleTitle?: string;
    }
  ): SuggestionItem[] => {
    const certId = activeCert?.id || user?.activeCertId || 'cna';
    const isPro = user?.planTier === 'paid';
    
    // 1. Gather historical data from local storage & user progress
    const history = user?.history || [];
    const recentScores = history
      .filter((h: any) => h.certId === certId)
      .slice(-5)
      .map((h: any) => h.score || 0);

    const averageRecentScore = recentScores.length > 0 
      ? Math.round(recentScores.reduce((a, b) => a + b, 0) / recentScores.length)
      : null;

    // 2. Read curricular states
    const completedModsStr = localStorage.getItem(`${certId}_completed_study_modules`);
    const completedModulesList: string[] = completedModsStr ? JSON.parse(completedModsStr) : [];
    
    const completedSkillsStr = localStorage.getItem(`${certId}_completed_skills`);
    const completedSkillsList: string[] = completedSkillsStr ? JSON.parse(completedSkillsStr) : [];

    // 3. Count outstanding checkpoints
    const missedQsCount = user?.missedQuestions?.length || 0;
    
    // Establish pool of potential recommendations
    const recommendations: SuggestionItem[] = [];

    // --- CONDITION A: CRITICAL REMEDIATION (Recent score indicating weakness) ---
    const scoreUnderReview = context.score !== undefined ? context.score : (averageRecentScore !== null ? averageRecentScore : null);
    
    if (scoreUnderReview !== null && scoreUnderReview < 75) {
      // Recommend reviewing the core theoretical decks or flashcards
      recommendations.push({
        id: 'remedial_guide',
        type: 'guide',
        title: 'Theory Remediation Deck',
        badge: 'Critical Review',
        badgeColor: 'bg-rose-50 border-rose-100 text-rose-700 dark:bg-rose-950/20 dark:border-rose-900/50 dark:text-rose-400',
        description: 'Re-calibrate your clinical understanding by examining the full-text syllabus with clinical observations.',
        rationale: `Cognitive gaps detected under timed conditions (Score: ${scoreUnderReview}%). Re-reading foundational core slides helps consolidate clinical pathways before next drill attempt.`,
        actionText: 'Open Theoretical Guide',
        targetUrl: `/${certId}/guide`,
        difficulty: 'Critical Drill',
        timeEstimate: '6-8 mins',
        engagementXP: 80
      });

      recommendations.push({
        id: 'remedial_flashcards',
        type: 'flashcards',
        title: 'Recall Reinforcement',
        badge: 'Recall Practice',
        badgeColor: 'bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-955/20 dark:border-amber-900/50 dark:text-amber-400',
        description: 'Practice high-frequency clinical concepts using spaced repetition (SRS) to commit parameters to memory.',
        rationale: 'Active recall activates neurological index cards. Spacings and promptings prevent board-style memory fatigue.',
        actionText: 'Review Flashcards',
        targetUrl: `/${certId}/flashcards`,
        difficulty: 'Beginner',
        timeEstimate: '5 mins',
        engagementXP: 60
      });

      // Recommend asking our AI Tutor
      recommendations.push({
        id: 'tutor_remedial',
        type: 'tutor',
        title: 'Cognitive AI Remediation Session',
        badge: 'AI Clinical Tutor',
        badgeColor: 'bg-purple-50 border-purple-100 text-purple-700 dark:bg-purple-955/20 dark:border-purple-900/50 dark:text-purple-400',
        description: 'Get an automated, custom-tailored feedback session explaining the common clinical pitfalls of your weak areas.',
        rationale: 'Personalized interactive feedback maps perfectly to NCSBN critical judgement guidelines.',
        actionText: 'Consult AI Clinical Coach',
        targetUrl: `/${certId}/tutor`,
        difficulty: 'Intermediate',
        timeEstimate: '4 mins',
        engagementXP: 100
      });
    }

    // --- CONDITION B: VULNERABILITY RECOVERY (Missed questions in vault) ---
    if (missedQsCount > 0) {
      recommendations.push({
        id: 'missed_remediation',
        type: 'quiz',
        title: 'Mistake Vault Clear Run',
        badge: 'Remediation Active',
        badgeColor: 'bg-red-50 border-red-100 text-red-700 dark:bg-red-955/20 dark:border-red-900/50 dark:text-red-400',
        description: `Clear ${missedQsCount} logical errors stored in your mistake registry. Overriding these errors requires two consecutive correct answers.`,
        rationale: 'The surest way to boost licensing score is systematically isolating and conquering past wrong choices.',
        actionText: 'Enter Mistake Vault',
        targetUrl: `/${certId}/missed`,
        difficulty: 'Critical Drill',
        timeEstimate: `${Math.min(10, Math.ceil(missedQsCount * 0.8))} mins`,
        engagementXP: 120
      });
    }

    // --- CONDITION C: PROGRESSION BY COGNITIVE SOURCE TYPE ---
    // Rule based on what the user just finished to suggest an appropriate complementary module
    if (context.source === 'guide') {
      // Left study guide, suggest testing their knowledge via targeted Recall Drill
      recommendations.push({
        id: 'post_guide_quiz',
        type: 'quiz',
        title: `Recall Quiz: Clinical Reinforcement`,
        badge: 'Board Calibration',
        badgeColor: 'bg-indigo-50 border-indigo-100 text-indigo-700 dark:bg-indigo-950/20 dark:border-indigo-900/50 dark:text-indigo-400',
        description: 'Put your newly synthesized lesson knowledge to the test. Answer board-style calibration multiple choice questions.',
        rationale: 'Testing yourself immediately after reading standard modules increases retention by 300% (The Testing Effect).',
        actionText: 'Launch Practice Quiz',
        targetUrl: `/${certId}/practice`,
        difficulty: 'Intermediate',
        timeEstimate: '5-10 mins',
        engagementXP: 90
      });

      // Suggest skills lab to match theoretical knowledge with clinical checklists
      recommendations.push({
        id: 'post_guide_skills',
        type: 'skills',
        title: 'Procedural Skills Deck',
        badge: 'Physical Checkpoints',
        badgeColor: 'bg-emerald-50 border-emerald-100 text-emerald-750 dark:bg-emerald-950/20 dark:border-emerald-900/50 dark:text-emerald-400',
        description: 'Verify your hands-on nursing competence. Study the diagnostic procedural check steps for care practices.',
        rationale: 'Casting theory into practical physical maneuvers prepares candidates for state proctored physical tests.',
        actionText: 'Launch Skills Lab',
        targetUrl: `/${certId}/skills`,
        difficulty: 'Intermediate',
        timeEstimate: '7 mins',
        engagementXP: 80
      });
    } 
    else if (context.source === 'quiz') {
      // Just finished a quiz, suggest stepping up to a full simulator run or flashcards if score is good.
      if (scoreUnderReview !== null && scoreUnderReview >= 75) {
        recommendations.push({
          id: 'post_quiz_exam',
          type: 'exam',
          title: 'Board-Style Simulation Arena',
          badge: 'Jurisdictional Run',
          badgeColor: 'bg-slate-900 border-slate-700 text-slate-100 dark:bg-slate-950/50 dark:border-slate-800 dark:text-slate-200',
          description: 'Try the ultimate test. Launch a structured state-specific exam with official timer controls.',
          rationale: 'Demonstrated high core recall (Score: ' + scoreUnderReview + '%). Ready to test compliance and stamina under standard board constraints.',
          actionText: 'Launch Simulator Arena',
          targetUrl: `/${certId}/exam`,
          difficulty: 'Advanced',
          timeEstimate: '60 mins',
          engagementXP: 250
        });
      }

      recommendations.push({
        id: 'post_quiz_math',
        type: 'simulator',
        title: 'Clinical Math SandBox',
        badge: 'Lab Calculations',
        badgeColor: 'bg-cyan-50 border-cyan-100 text-cyan-705 dark:bg-cyan-955/20 dark:border-cyan-900/50 dark:text-cyan-400',
        description: 'Sharpen your intake, output, metric weight systems, and procedural fluid calculation competencies.',
        rationale: 'Clinically rigorous dosing and measurement scenarios require active calculation workouts on scratchpads.',
        actionText: 'Launch Math Lab',
        targetUrl: `/${certId}/simulator`,
        difficulty: 'Intermediate',
        timeEstimate: '5 mins',
        engagementXP: 80
      });
    }
    else if (context.source === 'exam') {
      // Finished a full mock exam! Recommend reviewing flashcard mnemonics or listening to auditory podcasts to rest cognitive pathways.
      recommendations.push({
        id: 'post_exam_podcast',
        type: 'podcasts',
        title: 'Visual & Sensory Rest: PodCasts',
        badge: 'Auditory Study',
        badgeColor: 'bg-violet-50 border-violet-100 text-violet-700 dark:bg-violet-955/20 dark:border-violet-900/50 dark:text-violet-400',
        description: 'Relax your eyes while keeping your clinical cortex active. Listen to high-yield clinical focus review audio files.',
        rationale: 'Avoid visual burnout. Switching channels to auditory learning aids mnemonic retention on-the-go.',
        actionText: 'Listen to PodCasts',
        targetUrl: `/${certId}/podcasts`,
        difficulty: 'Beginner',
        timeEstimate: '8-15 mins',
        engagementXP: 70
      });

      recommendations.push({
        id: 'post_exam_tutor',
        type: 'tutor',
        title: 'Predictive Evaluation Hub',
        badge: 'Smart Diagnostic',
        badgeColor: 'bg-fuchsia-50 border-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-955/20 dark:border-fuchsia-900/50 dark:text-fuchsia-405',
        description: 'Analyze your licensing readiness probability metrics and request an AI diagnostic breakdown.',
        rationale: 'Reviewing performance reports exposes specific clinical domains that require active remediation.',
        actionText: 'Check Readiness forecast',
        targetUrl: `/${certId}/dashboard`,
        difficulty: 'Intermediate',
        timeEstimate: '2 mins',
        engagementXP: 50
      });
    }
    else if (context.source === 'simulator') { // Math Lab
      // Math calculator completed, recommend a procedural skill slide or diagnostic tool
      recommendations.push({
        id: 'post_math_skills',
        type: 'skills',
        title: 'Metrics-Based Skill slide',
        badge: 'Measurement Check',
        badgeColor: 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-900/50 dark:text-emerald-400',
        description: 'Examine step-by-step physical protocols for "Vital Sign Measurement" or "Urinary Output Calculations".',
        rationale: 'Theoretical fluid calculations must be mapped directly to actual clinical nursing protocols to prevent medical error.',
        actionText: 'Go to Skills Lab',
        targetUrl: `/${certId}/skills`,
        difficulty: 'Intermediate',
        timeEstimate: '8 mins',
        engagementXP: 90
      });
    }
    else if (context.source === 'skills') {
      // Skills finished! Suggest a quiz to test compliance of steps order sequence
      recommendations.push({
        id: 'post_skills_quiz',
        type: 'quiz',
        title: 'Procedural Compliance Recall',
        badge: 'Compliance Drill',
        badgeColor: 'bg-indigo-50 border-indigo-100 text-indigo-750 dark:bg-indigo-950/20 dark:border-indigo-900/50 dark:text-indigo-400',
        description: 'Test your understanding of priority safety steps, patient identification, and documentation checkpoints.',
        rationale: 'Ensuring correct step sequence is the difference between a pass and a fail in regional physical board exams.',
        actionText: 'Take Safety Quiz',
        targetUrl: `/${certId}/practice`,
        difficulty: 'Intermediate',
        timeEstimate: '6 mins',
        engagementXP: 100
      });
    }

    // --- CONDITION D: FILLING OUT STANDING OUTSTANDING REGISTRY HOLES ---
    // If we have incomplete modules or skills, let's suggest them
    if (completedModulesList.length < 5) {
      recommendations.push({
        id: 'unstarted_guide',
        type: 'guide',
        title: 'Principles & Delegations Deck',
        badge: 'Syllabus Core',
        badgeColor: 'bg-teal-50 border-teal-100 text-teal-700 dark:bg-teal-955/20 dark:border-teal-900/50 dark:text-teal-400',
        description: 'Uncover vital legal boundaries, delegation requirements, patient privacy rules (HIPAA), and documentation logs.',
        rationale: 'Critical structural syllabus. Licensing tests assess delegation rights with extremely strict scrutiny.',
        actionText: 'Access Syllabus Deck',
        targetUrl: `/${certId}/guide`,
        difficulty: 'Beginner',
        timeEstimate: '8 mins',
        engagementXP: 80
      });
    }

    if (completedSkillsList.length < 4) {
      recommendations.push({
        id: 'unstarted_skills',
        type: 'skills',
        title: 'Safety, Hygiene & PPE Lab',
        badge: 'Essential Procedure',
        badgeColor: 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-950/20 dark:border-emerald-900/50 dark:text-emerald-400',
        description: 'Master handwashing friction parameters, gloves application standards, and sterile zone guidelines.',
        rationale: 'Safety and handwashing is marked as a critical step in all US states. A single infection check skip triggers immediate fail.',
        actionText: 'Launch Isolation Lab',
        targetUrl: `/${certId}/skills`,
        difficulty: 'Beginner',
        timeEstimate: '6 mins',
        engagementXP: 100
      });
    }

    // Podcast suggestion for engagement
    recommendations.push({
      id: 'engagement_podcast',
      type: 'podcasts',
      title: 'Nursing Ethics & Legal Boundaries',
      badge: 'Auditory Prep',
      badgeColor: 'bg-violet-50 border-violet-100 text-violet-700 dark:bg-violet-955/20 dark:border-violet-900/50 dark:text-violet-400',
      description: 'Stream concise key conceptual reviews. Perfect for listening in your commute, car, or rest periods.',
      rationale: 'Bimodal auditory learning consolidates medical pathways in different neural compartments than text-oriented drills.',
      actionText: 'Play Audio Podcast',
      targetUrl: `/${certId}/podcasts`,
      difficulty: 'Beginner',
      timeEstimate: '11 mins',
      engagementXP: 90
    });

    // AI registry Search suggestion
    recommendations.push({
      id: 'engagement_search',
      type: 'search',
      title: 'Registry Search & Cross-Reference',
      badge: 'Smart Registry Catalog',
      badgeColor: 'bg-rose-50 border-rose-100 text-rose-700 dark:bg-rose-955/20 dark:border-rose-900/50 dark:text-rose-450',
      description: 'Search official state board registries or ask queries to locate exact nursing criteria or formulas.',
      rationale: 'Active clinical search and inquiry-based learning matches advanced educational board paradigms.',
      actionText: 'Query Registry',
      targetUrl: `/${certId}/search`,
      difficulty: 'Intermediate',
      timeEstimate: '3 mins',
      engagementXP: 60
    });

    // 4. Sort and filter the best recommendations based on priority mapping.
    // We prioritize:
    // 1. Vault clearances (if any)
    // 2. Critical score remedial steps (if they failed)
    // 3. Post-completion transition rules
    // 4. Incomplete syllabus work
    // 5. Engagement assets (Podcast, Search)
    const priorityMap = {
      'missed_remediation': 1,
      'remedial_guide': 2,
      'tutor_remedial': 3,
      'remedial_flashcards': 4,
      'post_guide_quiz': 5,
      'post_guide_skills': 6,
      'post_quiz_exam': 7,
      'post_quiz_math': 8,
      'post_exam_podcast': 9,
      'post_exam_tutor': 10,
      'post_math_skills': 11,
      'post_skills_quiz': 12,
      'unstarted_guide': 13,
      'unstarted_skills': 14,
      'engagement_podcast': 15,
      'engagement_search': 16
    } as any;

    const sortedRecs = recommendations
      .filter((rec, index, self) => self.findIndex(r => r.id === rec.id) === index) // Unique elements
      .sort((a, b) => {
        const priorityA = priorityMap[a.id] ?? 99;
        const priorityB = priorityMap[b.id] ?? 99;
        return priorityA - priorityB;
      });

    // Return the top 2 suggestions to make it clean, informative, and engaging!
    return sortedRecs.slice(0, 2);
  }
};
